import React, { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { Popover, TableCell, Typography } from '@mui/material'
import { useAuthContext } from '../../../../../hooks/useAuthContext'
import {
  ACTIVITY_TYPES,
  CURRENCY_COUNTRY_CODE,
  FIELD_TYPES,
  FORMATS,
  STANDARD_COLORS,
} from '../../../../../utility/constants'
import { listDataService } from '../../../../../services/listDataService'
import AutoCompleteField from '../../AutoCompleteField'
import MultiSelectField from '../../MultiSelectField'
import FileUploadField from '../../FileUploadField'
import FilePreviewField from '../../FilePreviewField'
import CurrencyField from '../../CurrencyField'
import BigTextField from '../../BigTextField'
import TextOrNumberField from '../../TextOrNumberField'
import DateAndDateTime from '../../DateAndDateTime'
import { fileServices } from '../../../../../services/fileServices'
import moment from 'moment'
import { DROPDOWN_DEFAULT_VALUE } from '../../../utils/autoCompute'
import WrapWithReviewSwitch from '../../../Components/WrapWithReviewSwitch'
import { getCommentId, getFieldId, getKeyOfValue, getReviewId } from '../../../utils/utils'
import { isReviewSwitchDisabled } from '../../../utils/fieldUtil'
import { useFieldCurrencyMap, useProcessContext, useAllValues } from '../../../../../store'
import { getColumnWidth } from '../../../../../../src/utility/columnWidth.js'

import { ON_CHANGE_DATA_TYPE } from '../TableField'
import { replacePlaceholders } from '../../../utils/placeholderUtil.js'
import { extractDisplayName } from '../../../../../utility/fileUtil.js'
import LongMenu from '../../LongMenu.js'
import { CircularProgress, IconButton } from '@mui/material'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import FileViewer from '../../../../../components/Dashboard/PreviewComponent/components/FileViewer'
import FieldHistory from '../../../../../components/DataEntry/FieldHistory.js'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined'
import { HighlightOff } from '@mui/icons-material'
import { formatCurrency } from '../../../utils/currency.js'
import { formatUserFullName } from '../../../../../utility/userName'
import TimeStampedBigTextField from '../../TimeStampedBigTextField.js'
import { indexedDbStorage } from '../../../../../utility/indexedDbStorage.js'
import { applyUserFilter } from '../../../../../utility/dataEntry/formData'

const clampNumericString = (input, places) => {
  if (input === '' || input === null || input === undefined) return ''
  const str = String(input)
  const match = str.match(/^(-?\d+)(?:\.(\d*))?/)
  if (!match) return ''
  const [, intPart, fracPart = ''] = match
  if (places === null || places === undefined) return match[0]
  if (places === 0) return intPart
  const clampedFrac = fracPart.slice(0, places)
  return clampedFrac.length ? `${intPart}.${clampedFrac}` : intPart
}

const TableCellWrapper = ({
  field,
  rowId,
  isSelectable,
  isSelected,
  isImported,
  rowData,
  rowReview,
  rowComment,
  previousReviewDataOfCurrentRow,
  previousReviewCommentsDataOfCurrentRow,
  previousAllReviewsOfCurrentRow,
  onChangeValue,
  columnWidth = '100%',
  helperText = null,
  isFieldDisabled = () => {},
  syncWithParent = () => {},
  isReviewSwitchHidden = () => {},
  leftOffset,
  isFrozen,
  inputSx = {},
}) => {
  const { user } = useAuthContext()
  const [listData, setListData] = useState([])
  const [value, setValue] = useState(rowData[getFieldId(field.id)])

  // Add refs for width calculation
  const tableCellRef = useRef(null)
  const [calculatedMaxWidth, setCalculatedMaxWidth] = useState(null)

  const fieldCurrencyMap = useFieldCurrencyMap((state) => state.fieldCurrencyMap)

  // pop over
  const [popOverAnchorEl, setPopOVerAnchorEl] = useState(null)
  const [popOverOpen, setPopOverOpen] = useState(false)
  const [popOverId, setPopOverId] = useState(undefined)
  const [popOverContentWidth, setPopOverContentWidth] = useState(null)

  /* process context */
  const processContext = useProcessContext((state) => state.processContext)
  const activityType = processContext?.activityTemplate?.activity_type
  const processStatus = processContext?.processData?.status
  const defaultCurrency = processContext?.companyDetails?.default_currency_code
  const version = processContext?.processData?.version
  const listDataMetadata = processContext?.listDataMetadata
  const dynamicListData = processContext?.dynamicListData
  const teamsList = useProcessContext((state) => state.processContext.dynamicListData.teamsList)

  const allValues = useAllValues((state) => state.allValues)

  const [listDataSynced, setListDataSynced] = useState([])

  useEffect(() => {
    setListDataSynced(syncWithParent(field, rowData, listData, teamsList))
  }, [field, rowData, listData, teamsList])

  useEffect(() => {
    let isMounted = true

    ;(async () => {
      if (field.data_structure != FIELD_TYPES.LIST && field.data_structure != FIELD_TYPES.LIST_MULTI) {
        return
      }

      // Dynamic lists are derived from process context and should not use indexedDB list cache.
      if (field.list_id == null) {
        const meta = field.dynamic_field_type
        const allData = processContext?.dynamicListData

        if (meta?.type == 'teams') {
          let nextListData = []

          if (meta?.supertype) {
            nextListData = allData?.teamsList
              ?.filter((it) => meta.supertype.includes(it.metadata.team_subtype))
              ?.filter((it) => meta?.subtypes.includes(it.metadata.team_type))
          } else {
            nextListData = allData?.teamsList?.filter(
              (it) =>
                meta?.subtypes?.includes(it.metadata.team_type) &&
                it.metadata.team_subtype == null,
            )
          }

          if (isMounted) setListData(nextListData || [])
        } else if (meta?.type == 'users') {
          const filteredUsers = applyUserFilter(allData?.usersList || [], meta)
          if (isMounted) setListData(filteredUsers)
        } else {
          console.error('Dynamic Field Implementation Pending for ' + meta?.type)
          if (isMounted) setListData([])
        }
        return
      }

      const listDataId = `list_data_${field.list_id}`
      const listDataJson = await indexedDbStorage.getItem(listDataId)

      if (listDataJson) {
        if (isMounted) setListData(listDataJson)
        return
      }

      const listData = await listDataService.getData(user, field.list_id)
      if (isMounted) setListData(listData.data)
      await indexedDbStorage.setItem(listDataId, listData.data)
    })()

    return () => {
      isMounted = false
    }
  }, [
    field.id,
    field.list_id,
    field.data_structure,
    JSON.stringify(field.dynamic_field_type || null),
    processContext?.dynamicListData,
    user,
  ])

  const selectedValues = React.useMemo(() => {
    return listDataSynced.find((it) => it.id == value)
  }, [value, listDataSynced])

  const isFieldFinallyDisabled = useCallback(() => {
    if (field?.is_read_only || activityType == ACTIVITY_TYPES.REVIEW) {
      return true
    }
    const isDisabled = isFieldDisabled(
      field,
      rowId,
      isSelected,
      isImported,
      previousReviewDataOfCurrentRow || {},
      rowReview,
    )
    return isDisabled
  }, [isSelected, rowData.updatedAt])

  const onChange = async (e, v) => {
    switch (field.data_structure) {
      case FIELD_TYPES.LIST:
        setValue(v?.id || DROPDOWN_DEFAULT_VALUE)
        break
      case FIELD_TYPES.LIST_MULTI:
        setValue(getTargetValues(v))
        break
      case FIELD_TYPES.FILE:
        const file = e.target.files[0]
        const result = (await fileServices.uploadFile(user, file)).data
        const filePath = result?.filePath || ''
        const fileName = filePath.substring(filePath.lastIndexOf('/') + 1)
        setValue(fileName || '')
        break
      case FIELD_TYPES.CURRENCY:
        if (e.floatValue !== undefined) {
          setValue(e.floatValue)
        } else {
          setValue(0)
        }
        break
      case FIELD_TYPES.BIGTEXT:
        setValue(e.target.value || '')
        break
      case FIELD_TYPES.TEXT:
        setValue(e.target.value || '')
        break
      case FIELD_TYPES.NUMBER:
        {
          const raw = e.target.value
          // Allow clearing
          if (raw === '' || raw === null || raw === undefined) {
            setValue('')
            break
          }

          // Preserve leading zeros mode (e.g., phone numbers)
          const preserveLeadingZeros = true
          if (preserveLeadingZeros) {
            setValue(raw)
            break
          }

          const places =
            typeof field?.num_decimal_places === 'number'
              ? field.num_decimal_places
              : field?.num_decimal_places === 0
                ? 0
                : null

          const clampedValue = clampNumericString(raw, places)
          let num = Number.parseFloat(clampedValue)
          if (!Number.isFinite(num)) num = 0

          setValue(num)
        }
        break
      case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
        setValue(e.target.value || '')
        break
      case FIELD_TYPES.DATE:
      case FIELD_TYPES.DATETIME:
        setValue(e?.format('YYYY-MM-DD HH:mm:ss'))
        break
      default:
        console.log(`onChange - ${field.data_structure} is not supported!`)
    }
  }

  const fieldTemplateNamesForPlaceHolders = useMemo(() => {
    return (
      processContext?.allFields?.reduce((acc, cur) => {
        return { ...acc, [cur.id]: `[[${cur.field_template_name}]]` }
      }, {}) || {}
    )
  }, [processContext?.allFields])

  const getTargetValues = (selectedItems) => {
    if (Array.isArray(selectedItems) && selectedItems.length) {
      return selectedItems.map((ele) => ele.id)
    }
    return DROPDOWN_DEFAULT_VALUE
  }

  const getActualValue = (value) => {
    switch (field.data_structure) {
      case FIELD_TYPES.LIST:
        let listDataValue = listDataSynced.find((it) => it.id == value)?.value

        let actualValue = replacePlaceholders(
          listDataValue,
          fieldTemplateNamesForPlaceHolders,
          rowData,
          listDataMetadata,
        )
        return actualValue

      case FIELD_TYPES.LIST_MULTI:
        let listMultiActualValue = Array.isArray(value)
          ? value?.map((val) => listDataSynced.find((it) => it.id == val)?.value)
          : ['']

        return listMultiActualValue.join(', ')

      case FIELD_TYPES.CURRENCY:
        return formatCurrency(
          value,
          getKeyOfValue(CURRENCY_COUNTRY_CODE, fieldCurrencyMap[field.id]),
          {
            defaultCurrency: defaultCurrency,
          },
        )

      case FIELD_TYPES.DATE:
        const returnDate = moment(new Date(value)).format(FORMATS.DATE)
        return returnDate === 'Invalid date' ? '' : returnDate

      case FIELD_TYPES.DATETIME:
        const returnDateTime = moment(new Date(value)).format(FORMATS.DATETIME)
        return returnDateTime === 'Invalid date' ? '' : returnDateTime

      case FIELD_TYPES.NUMBER: {
        const preserveLeadingZeros = true
        if (preserveLeadingZeros) {
          return value ?? ''
        }
        // Normalize display with configured decimal places if provided
        const places =
          typeof field?.num_decimal_places === 'number'
            ? field.num_decimal_places
            : field?.num_decimal_places === 0
              ? 0
              : null

        const formatted = clampNumericString(value, places)
        return formatted
      }

      case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
        if (!Array.isArray(value)) {
          // console.error('value is not an array', value)
          return ''
        }

        const latestEntry = value && value[0] ? value[0] : null
        const user = latestEntry?.user_id
          ? dynamicListData?.users?.find((u) => u.id === latestEntry.user_id)
          : null
        const username = user ? formatUserFullName(user) : null
        return `${latestEntry.timestamp} : ${latestEntry.text} - ${username}`

      case FIELD_TYPES.FILE:
        return extractDisplayName(value)

      default:
        return value
    }
  }

  const renderFields = () => {
    switch (field.data_structure) {
      case FIELD_TYPES.LIST:
        return (
          <AutoCompleteField
            field={field}
            fieldId={field.id}
            value={selectedValues || null}
            onChange={onChange}
            options={listDataSynced}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled()}
            helperText={helperText}
            autoFocus={true}
            inputSx={inputSx}
          />
        )
      case FIELD_TYPES.LIST_MULTI:
        return (
          <MultiSelectField
            field={field}
            helperText={helperText}
            id={field.id}
            onChange={onChange}
            options={listDataSynced}
            value={isFieldFinallyDisabled() ? rowData[getFieldId(field.id)] : value}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled()}
            autoFocus={true}
            inputSx={inputSx}
          />
        )
      case FIELD_TYPES.FILE:
        return !value ? (
          <FileUploadField
            field={field}
            id={field.id}
            onChange={onChange}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled()}
            helperText={helperText}
            tableField={true}
            inputSx={inputSx}
          />
        ) : (
          <FilePreviewField
            field={field}
            id={field.id}
            value={value}
            placeholder={field?.field_template_name}
            download={() => fileServices.downloadFile(user, value)}
            clear={(e) => setValue('')}
            disabled={isFieldFinallyDisabled()}
            helperText={helperText}
            isTableCell={true}
            inputSx={inputSx}
          />
        )
      case FIELD_TYPES.CURRENCY:
        return (
          <CurrencyField
            field={field}
            id={field.id}
            value={isFieldFinallyDisabled() ? rowData[getFieldId(field.id)] : value}
            onChange={onChange}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled()}
            helperText={helperText}
            autoFocus={true}
            inputSx={inputSx}
          />
        )
      case FIELD_TYPES.BIGTEXT:
        return (
          <BigTextField
            field={field}
            id={field.id}
            value={isFieldFinallyDisabled() ? rowData[getFieldId(field.id)] : value}
            onChange={onChange}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled()}
            helperText={helperText}
            autoFocus={true}
            inputSx={inputSx}
          />
        )
      case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
        return (
          <TimeStampedBigTextField
            field={field}
            id={field.id}
            value={isFieldFinallyDisabled() ? rowData[getFieldId(field.id)] : value}
            onChange={onChange}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled()}
            helperText={helperText}
            autoFocus={true}
            inputSx={inputSx}
          />
        )
      case FIELD_TYPES.TEXT:
      case FIELD_TYPES.NUMBER:
        return (
          <TextOrNumberField
            field={field}
            id={field.id}
            type={field.data_structure}
            onChange={onChange}
            value={isFieldFinallyDisabled() ? rowData[getFieldId(field.id)] : value}
            placeholder={field?.field_template_name}
            disabled={isFieldFinallyDisabled()}
            helperText={helperText}
            autoFocus={true}
            inputSx={inputSx}
          />
        )
      case FIELD_TYPES.DATE:
      case FIELD_TYPES.DATETIME:
        return (
          <DateAndDateTime
            field={field}
            id={field.id}
            value={isFieldFinallyDisabled() ? rowData[getFieldId(field.id)] : value}
            onChange={onChange}
            placeholder={field?.field_template_name}
            type={field?.data_structure}
            disabled={isFieldFinallyDisabled()}
            helperText={helperText}
            tableField={true}
            autoFocus={true}
            inputSx={inputSx}
          />
        )
      default:
        return (
          <p style={{ color: 'red' }}>{field.data_structure} - Not a valid field to render!!</p>
        )
    }
  }

  const isReviewDisabled = useCallback((field) => {
    return isReviewSwitchDisabled(
      activityType,
      processStatus,
      processContext?.processData?.modified_by,
      user.id,
    )
  }, [])

  const isSubsequentDataEntry = (activityType, version) => {
    return activityType == ACTIVITY_TYPES.DATA_ENTRY && version > 1
  }

  // prefer live row review first, then fall back to previous review (for subsequent DE)
  const getEffectiveReviewForCell = () => {
    const live = rowReview ? rowReview[getReviewId(field.id)] : undefined
    if (live !== undefined) return live
    if (activityType === ACTIVITY_TYPES.DATA_ENTRY && version > 1) {
      return previousReviewDataOfCurrentRow
        ? previousReviewDataOfCurrentRow[getReviewId(field.id)]
        : undefined
    }
    return live
  }

  const popOverHandleClick = (event, id, width) => {
    if (activityType != ACTIVITY_TYPES.REVIEW || field.data_structure === FIELD_TYPES.FILE) {
      setPopOVerAnchorEl(event.currentTarget)
      setPopOverId(id)
      setPopOverOpen(true)
      setPopOverContentWidth(width)
      const current = rowData[getFieldId(field.id)]
      if (field.data_structure == FIELD_TYPES.NUMBER) {
        const preserveLeadingZeros = true
        if (preserveLeadingZeros) {
          setValue(current === undefined || current === null ? '' : String(current))
        } else {
          const places =
            typeof field?.num_decimal_places == 'number'
              ? field.num_decimal_places
              : field?.num_decimal_places === 0
                ? 0
                : null
          const clamped = clampNumericString(current, places)
          if (clamped === '') {
            setValue('')
          } else {
            const num = Number.parseFloat(clamped)
            setValue(Number.isFinite(num) ? num : 0)
          }
        }
      } else {
        setValue(current)
      }
    }
  }

  const popOverHandleClose = () => {
    if (isSelectable && isSelected && !field.is_read_only) {
      if (value !== rowData[getFieldId(field.id)]) {
        onChangeValue(field.id, value, ON_CHANGE_DATA_TYPE.VALUE_DATA)
      }
    } else if (!isSelectable && !field.is_read_only) {
      if (value !== rowData[getFieldId(field.id)]) {
        onChangeValue(field.id, value, ON_CHANGE_DATA_TYPE.VALUE_DATA)
      }
    }

    setPopOVerAnchorEl(null)
    setPopOverId(undefined)
    setPopOverOpen(false)
    setPopOverContentWidth(null)
  }

  const popOverOnEnterKeyPress = (e, data_structure) => {
    if (
      e.key === 'Enter' &&
      [FIELD_TYPES.BIGTEXT, FIELD_TYPES.TIMESTAMPED_BIGTEXT].includes(data_structure)
    ) {
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      popOverHandleClose()
    }
  }

  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false)

  const handlePreviewClick = (fileKey) => {
    setIsFileViewerOpen(true)
  }

  // Calculate width based on container
  useEffect(() => {
    const calculateWidth = () => {
      if (tableCellRef.current) {
        const tableCellWidth = tableCellRef.current.offsetWidth

        // Calculate available width for the field content
        // Account for padding, borders, and action buttons
        const reviewSwitchWidth = isReviewSwitchHidden(field) ? 0 : 80
        const fileActionsWidth =
          field.data_structure === FIELD_TYPES.FILE && rowData[getFieldId(field.id)] ? 75 : 0
        const fieldHistoryWidth = 10 // approximate width for field history wrapper

        const availableWidth =
          tableCellWidth - reviewSwitchWidth - fileActionsWidth - fieldHistoryWidth

        setCalculatedMaxWidth(Math.max(availableWidth, 100)) // minimum 100px
      }
    }

    // Calculate on mount and when dependencies change
    calculateWidth()

    // Recalculate on window resize
    const handleResize = () => {
      setTimeout(calculateWidth, 100) // debounce
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [field, rowData, isReviewSwitchHidden])

  // Use ResizeObserver for more accurate detection of size changes
  useEffect(() => {
    if (!tableCellRef.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width } = entry.contentRect

        // Calculate individual component widths
        const reviewSwitchWidth = isReviewSwitchHidden(field)
          ? 0
          : field.data_structure === FIELD_TYPES.FILE
            ? 80
            : 48
        const hasFileActions =
          field.data_structure === FIELD_TYPES.FILE && rowData[getFieldId(field.id)]
        const fileActionsWidth = hasFileActions ? 15 : 0

        // Calculate spacing/margin adjustments
        let spacingAdjustment = 0
        if (reviewSwitchWidth > 0 && fileActionsWidth > 0) {
          // Both components present - need spacing between them
          spacingAdjustment = 18
        } else if (field.data_structure === FIELD_TYPES.FILE && reviewSwitchWidth > 0) {
          // File field with review switch but no file actions - still need some spacing
          spacingAdjustment = 18
        }

        // Calculate final available width
        const totalReservedWidth = reviewSwitchWidth + fileActionsWidth - spacingAdjustment
        const paddingBuffer = 0 // buffer for padding/borders
        const availableWidth = width - totalReservedWidth - paddingBuffer

        // Ensure minimum width of 100px
        setCalculatedMaxWidth(Math.max(availableWidth, 100))
      }
    })

    resizeObserver.observe(tableCellRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [field, rowData, isReviewSwitchHidden])
  return (
    <>
      <TableCell
        ref={tableCellRef}
        aria-describedby={`popover_${field.id}__${rowId}`}
        sx={{
          backgroundColor: isFrozen ? 'lightgrey' : 'white',
          padding: '4px 4px',
          width: columnWidth,
          maxWidth: getColumnWidth(field, isReviewSwitchHidden),
          minWidth: getColumnWidth(field, isReviewSwitchHidden),
          position: isFrozen ? 'sticky' : 'static',
          left: isFrozen ? `${leftOffset}px` : 'auto',
          top: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        <WrapWithReviewSwitch
          field={field}
          review={getEffectiveReviewForCell()}
          comment={
            isSubsequentDataEntry(activityType, version)
              ? previousReviewCommentsDataOfCurrentRow
                ? previousReviewCommentsDataOfCurrentRow[getCommentId(field.id)]
                : ''
              : rowComment
                ? rowComment[getCommentId(field.id)]
                : ''
          }
          previousReviews={
            previousAllReviewsOfCurrentRow
              ? previousAllReviewsOfCurrentRow[getReviewId(field.id)]
              : []
          }
          onCommentChange={(cmt) => onChangeValue(field.id, cmt, ON_CHANGE_DATA_TYPE.COMMENT_DATA)}
          onReviewChange={(rvw) => onChangeValue(field.id, rvw, ON_CHANGE_DATA_TYPE.REVIEW_DATA)}
          disabled={isReviewDisabled(field)}
          row={rowId}
        >
          {/* <Tooltip title={helperText ? helperText : getActualValue(rowData[getFieldId(field.id)])}> */}
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <FieldHistory
              fieldId={`${field.id}__${rowId}`}
              rowId={rowId}
              field={field}
              listDataMetadata={listDataMetadata}
              dynamicListData={dynamicListData}
              processId={processContext?.processData?.id}
              allValues={allValues}
              helperText={helperText}
            >
              <div
                style={{
                  padding: '5px',
                  border: '1px solid',
                  borderColor:
                    helperText?.length || (helperText?.length > 0 && isSelectable && isSelected)
                      ? '#d32f2f'
                      : 'rgb(195 195 195)',
                  borderRadius: '4px',
                  backgroundColor: (() => {
                    const rvw = getEffectiveReviewForCell()
                    if (rvw === true) return STANDARD_COLORS.BG_READONLY
                    if (rvw === false) return STANDARD_COLORS.BG_EDITABLE
                    const disabled = isFieldFinallyDisabled()
                    if (disabled) return STANDARD_COLORS.BG_READONLY
                    return STANDARD_COLORS.BG_EDITABLE
                  })(),
                  height: 'auto',
                  minHeight: '30px',
                  maxHeight: '30px',
                  // Use calculated width
                  maxWidth: calculatedMaxWidth ? `${calculatedMaxWidth}px` : '100%',
                }}
                onClick={(e) => {
                  popOverHandleClick(
                    e,
                    `popover_${field.id}__${rowId}`,
                    e.currentTarget.getBoundingClientRect().width + 100,
                  )
                }}
              >
                <Typography
                  fontSize="small"
                  noWrap
                  align={
                    [FIELD_TYPES.CURRENCY, FIELD_TYPES.NUMBER].includes(field.data_structure)
                      ? 'right'
                      : 'left'
                  }
                  style={{
                    textOverflow: 'ellipsis',
                    color: (() => {
                      const rvw = getEffectiveReviewForCell()
                      if (rvw === true) return STANDARD_COLORS.TEXT_ACCEPTED
                      if (rvw === false) return STANDARD_COLORS.TEXT_REJECTED
                      return STANDARD_COLORS.TEXT_DEFAULT
                    })(),
                  }}
                >
                  {getActualValue(rowData[getFieldId(field.id)])}
                </Typography>
              </div>
            </FieldHistory>
            {field.data_structure === FIELD_TYPES.FILE && rowData[getFieldId(field.id)] && (
              <LongMenu
                options={[
                  {
                    label: 'Preview',
                    icon: (
                      <IconButton size="small">
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    ),
                    onClick: () => handlePreviewClick(value),
                  },
                  {
                    label: 'Download',
                    icon: (
                      <IconButton size="small">
                        {false ? (
                          <CircularProgress color="info" size={'25px'} />
                        ) : (
                          <GetAppOutlinedIcon color="info" size="small" />
                        )}
                      </IconButton>
                    ),
                    onClick: () => fileServices.downloadFile(user, value),
                  },
                  !isFieldFinallyDisabled() && {
                    label: 'Delete',
                    icon: (
                      <IconButton size="small" disabled={isFieldFinallyDisabled()}>
                        <DeleteOutlinedIcon disabled={isFieldFinallyDisabled()} size="small" />
                      </IconButton>
                    ),
                    onClick: isFieldFinallyDisabled()
                      ? undefined
                      : () => onChangeValue(field.id, '', ON_CHANGE_DATA_TYPE.VALUE_DATA),
                  },
                ].filter(Boolean)}
              />
            )}
          </div>

          {/* </Tooltip> */}
        </WrapWithReviewSwitch>
      </TableCell>
      {field.data_structure === FIELD_TYPES.FILE && (
        <FileViewer
          open={isFileViewerOpen}
          onClose={() => setIsFileViewerOpen(false)}
          fileKey={value}
          user={user}
          onDownload={() => fileServices.downloadFile(user, value)}
        />
      )}
      {!isFieldFinallyDisabled() && (
        <Popover
          id={popOverId}
          open={popOverOpen}
          anchorEl={popOverAnchorEl}
          onClose={popOverHandleClose}
          onKeyDownCapture={(e) => popOverOnEnterKeyPress(e, field.data_structure)}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
        >
          <div style={{ padding: '0px 12px 12px 12px', width: popOverContentWidth }}>
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton size="small" onClick={popOverHandleClose}>
                  <HighlightOff fontSize="10px" />
                </IconButton>
              </div>
              <div>{popOverOpen && renderFields()}</div>
            </>
          </div>
        </Popover>
      )}
    </>
  )
}

export default memo(
  TableCellWrapper,
  (prevProps, nextProps) =>
    prevProps.rowData[getFieldId(prevProps.field.id)] ===
      nextProps.rowData[getFieldId(nextProps.field.id)] &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.rowData.updatedAt === nextProps.rowData.updatedAt &&
    prevProps.rowReview?.updatedAt === nextProps.rowReview?.updatedAt &&
    prevProps.rowComment?.updatedAt === nextProps.rowComment?.updatedAt &&
    prevProps.isFrozen === nextProps.isFrozen &&
    prevProps.leftOffset === nextProps.leftOffset &&
    prevProps.helperText === nextProps.helperText,
)
